import React, { useState, useEffect } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getUserByEmail, updateUser } from "../lib/api/users";
import { User } from "../lib/types";

interface FormData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

const Profile: React.FC = () => {
  const { user } = useAuthenticator();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [googleProfilePicture, setGoogleProfilePicture] = useState<
    string | null
  >(null);
  const [formData, setFormData] = useState<FormData>({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    displayName: "",
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      // Get email from user object or stored tokens
      let email = "";
      if (user) {
        email = user.signInDetails?.loginId || "";
      }
      if (!email) {
        const idToken = sessionStorage.getItem("id_token");
        if (idToken) {
          try {
            const payload = JSON.parse(atob(idToken.split(".")[1]));
            email = payload.email || "";
          } catch (error) {
            console.error("Profile: Error decoding stored token:", error);
          }
        }
      }

      console.log("Profile: Got email from stored token:", email);

      if (!email) {
        throw new Error("No email found for user");
      }

      const userData = await getUserByEmail(email);
      if (userData) {
        setUserProfile(userData);
        setFormData({
          username: userData.username || "",
          email: userData.email || "",
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          displayName: userData.displayName || "",
        });

        // Load Google profile picture
        const googlePicture = getGoogleProfilePicture();
        if (googlePicture) {
          setGoogleProfilePicture(googlePicture);
          console.log("Profile: Set Google profile picture:", googlePicture);
        }
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getGoogleProfilePicture = (): string | null => {
    // Try to get profile picture from stored token
    const idToken = sessionStorage.getItem("id_token");
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        return payload.picture || null;
      } catch (error) {
        console.error("Profile: Error decoding stored token:", error);
      }
    }
    return null;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveProfile = async () => {
    setIsSaving(true);
    try {
      // Use Google profile picture
      const profilePictureUrl = getGoogleProfilePicture() || undefined;

      // Get email from user object or stored tokens
      let email = "";
      if (user) {
        email = user.signInDetails?.loginId || "";
      }
      if (!email) {
        const idToken = sessionStorage.getItem("id_token");
        if (idToken) {
          try {
            const payload = JSON.parse(atob(idToken.split(".")[1]));
            email = payload.email || "";
          } catch (error) {
            console.error("Profile: Error decoding stored token:", error);
          }
        }
      }

      if (!email) {
        throw new Error("No email found for user");
      }

      // Update user profile in API/DynamoDB
      const existingUser = await getUserByEmail(email);

      if (existingUser) {
        const updatedUser = await updateUser(existingUser.id, {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          displayName: formData.displayName,
          profilePictureUrl,
          profileCompleted: true,
        });

        if (updatedUser) {
          console.log("Profile updated successfully:", updatedUser);
          alert("Profile updated successfully!");
        } else {
          throw new Error("Failed to update profile");
        }
      } else {
        throw new Error("User profile not found");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.username.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.firstName.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.displayName.trim() !== ""
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Profile</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account information and preferences
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 border">
          {/* Profile Picture Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Profile Picture
            </label>
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-muted">
                {googleProfilePicture ? (
                  <img
                    src={googleProfilePicture}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Your Google profile picture is automatically used
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  To change your profile picture, update it in your Google
                  account
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-foreground mb-1"
              >
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label
                htmlFor="displayName"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6">
            <button
              onClick={saveProfile}
              disabled={!isFormValid() || isSaving}
              className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
